<template>
  <div class="superadmin-dashboard">
    <h2 class="page-title">Superadmin Dashboard</h2>
    <section>
      <div class="table-head-block">  
        <h3 class="sub-title">{{ $t('companyManagement.company', 1) }}</h3>
        <button @click="openCompanyProfile" class="add-company-btn">{{ $t('companyManagement.addCompany') }}</button>
      </div>
      <table class="companies-table">
        <thead>
          <tr>
            <th>Tenant</th>
            <th>{{ $t('companyManagement.company', 2) }}</th>
            <th>{{ $t('companyManagement.billingStatus') }}</th>
            <th>{{ $t('companyManagement.numberOfEmployees') }}</th>
            <th>{{ $t('companyManagement.totalMonthlyPayment') }}</th>
            <th>{{ $t('companyManagement.created_at') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="company in companies" :key="company.id" @click="openCompanyProfile(company)" style="cursor:pointer;">
            <td>{{ company.subdomain || '-' }}</td>
            <td>{{ company.name || '-' }}</td>
            <td>{{ company.billing_status || '-' }}</td>
            <td>{{ company.active_users || 0 }}</td>
            <td>{{ formatCurrency(getTotalMonthlyPayment(company)) }}</td>
            <td>{{ formatDate(company.created_at) }}</td>
          </tr>
        </tbody>
      </table>
      <CompanyProfileModal v-if="showCompanyModal" :company="selectedCompany" @close="closeCompanyProfile" @save="saveCompanyProfile" />
    </section>
    <section>
      <h3 class="sub-title">Invoices</h3>
      <table class="invoices-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>{{ $t('companyManagement.company', 1) }}</th>
            <th>{{ $t('companyManagement.issued') }}</th>
            <th>{{ $t('companyManagement.amount') }}</th>
            <th>{{ $t('companyManagement.description') }}</th>
            <th>{{ $t('companyManagement.paid') }}</th>
            <th>{{ $t('companyManagement.created_at') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="invoice in invoices" :key="invoice.id">
            <td>{{ invoice.id }}</td>
            <td>{{ invoice.company_name }}</td>
            <td>{{ formatDate(invoice.issue_date) }}</td>
            <td>{{ formatCurrency(invoice.amount) }}</td>
            <td>{{ invoice.description }}</td>
            <td>{{ invoice.is_paid ? 'Yes' : 'No' }}</td>
            <td>{{ formatDate(invoice.created_at) }}</td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useAuthStore } from '../stores/auth.js'
import { useI18n } from 'vue-i18n'
import CompanyProfileModal from './CompanyProfileModal.vue'

const companyDetails = ref({}) // Map tenant -> extra details
const companies = ref([]) // Store companies data
const invoices = ref([])
const authStore = useAuthStore()
const showCompanyModal = ref(false)
const selectedCompany = ref(null)
const { t } = useI18n()

function openCompanyProfile(company) {
  selectedCompany.value = { ...company }
  showCompanyModal.value = true
}

function closeCompanyProfile() {
  showCompanyModal.value = false
  selectedCompany.value = null
}

async function fetchCompanyDetails() {
  try {
    const companiesRes = await authStore.makeAuthenticatedRequest('/api/superadmin/companies/')
    if (companiesRes.ok) {
      companies.value = await companiesRes.json()
      // Populate companyDetails based on the fetched companies
      companyDetails.value = {}
      for (const c of companies.value) {
        if (c.schema_name) companyDetails.value[c.schema_name] = c
      }
    } else {
      console.error('Failed to fetch companies:', companiesRes.status)
    }
  } catch (err) {
    console.error('Error fetching companies:', err)
  }
}

async function saveCompanyProfile(updatedCompany) {
  try {
    let res;
    if (!updatedCompany.id) {
      res = await authStore.makeAuthenticatedRequest(
        '/api/superadmin/companies/',
        {
          method: 'POST',
          body: JSON.stringify(updatedCompany),
          headers: { 'Content-Type': 'application/json' }
        }
      )
    } else {
      res = await authStore.makeAuthenticatedRequest(
        `/api/superadmin/companies/${updatedCompany.id}/`,
        {
          method: 'PATCH',
          body: JSON.stringify(updatedCompany),
          headers: { 'Content-Type': 'application/json' }
        }
      )
    }
    if (!res.ok) {
      console.error('Error saving company:', res.status);
    }
  } catch (err) {
    console.error('Error saving company:', err)
  }
  showCompanyModal.value = false
  selectedCompany.value = null
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString()
}

function formatCurrency(amount) {
  return amount ? amount.toLocaleString('sv-SE', { style: 'currency', currency: 'SEK' }) : '-'
}

function getTotalMonthlyPayment(company) {
  const userCount = Number(company.active_users) || 0
  const price = Number(company.price_per_user) || 0
  return userCount * price
}

onMounted(async () => {
  await fetchCompanyDetails()
})
</script>

<style scoped>
.superadmin-dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  position: relative;
  margin: 1rem auto;
  padding: 1rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.table-head-block {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.add-company-btn {
  padding: 0.75rem 1.5rem;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}
.add-company-btn:hover {
  background: #218838;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(40, 167, 69, 0.4);
}

.companies-table, .invoices-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 2rem;
  border-radius: 5px;
}
.companies-table th,
.invoices-table th {
  background: #2196f3;
  color: #fff;
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
}

.companies-table td,
.invoices-table td {
  border: 1px solid #ddd;
  padding: 0.5rem 1rem;
  color: #ffffff;
  text-align: left;
}
th {
  background: #f5f5f5;
}
</style>